import React from "react";
import "./InteractiveCard.css";
import type { CardBrand } from "@/utils/cardValidation";
import { BrandIcon } from "./BrandLogos";

interface InteractiveCardProps {
  cardNumber: string;
  cardHolder: string;
  expiryMonth: string;
  expiryYear: string;
  cvv: string;
  brand: CardBrand;
  isFlipped: boolean;
  isInteractiveMode?: boolean;
  formConfig?: any;
  onChange?: {
    cardNumber: (e: React.ChangeEvent<HTMLInputElement>) => void;
    cardHolder: (e: React.ChangeEvent<HTMLInputElement>) => void;
    expiryMonth: (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => void;
    expiryYear: (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => void;
    cvv: (e: React.ChangeEvent<HTMLInputElement>) => void;
  };
  onFocus?: {
    cvv: () => void;
    blur: () => void;
  };
}

export default function InteractiveCard({
  cardNumber,
  cardHolder,
  expiryMonth,
  expiryYear,
  cvv,
  brand,
  isFlipped,
  isInteractiveMode = false,
  formConfig,
  onChange,
  onFocus,
}: InteractiveCardProps) {
  // AMEX keeps CVV on the front
  const showCvvOnFront = brand === "amex";
  const effectivelyFlipped = isFlipped && !showCvvOnFront;

  // Masking functions for display
  const displayPan = cardNumber || "#### #### #### ####";
  const displayName = cardHolder || "NOMBRE DEL TITULAR";
  const displayExp =
    expiryMonth || expiryYear
      ? `${expiryMonth || "MM"}/${expiryYear ? expiryYear.slice(-2) : "AA"}`
      : "MM/AA";

  const brandClass = `payment-card-bg-${brand === "unknown" ? "default" : brand}`;

  // Helper to render either an input or a span
  const renderField = (
    value: string,
    displayValue: string,
    placeholder: string,
    fieldKey: "cardNumber" | "cardHolder" | "cvv",
    className: string,
    maxLength?: number
  ) => {
    if (isInteractiveMode && formConfig && onChange) {
      return (
        <input
          id={formConfig.fields[fieldKey]?.id ?? fieldKey}
          name={formConfig.fields[fieldKey]?.name ?? fieldKey}
          className={`${className} payment-card-input`}
          value={value}
          onChange={onChange[fieldKey]}
          onFocus={fieldKey === "cvv" ? onFocus?.cvv : onFocus?.blur}
          onBlur={onFocus?.blur}
          placeholder={placeholder}
          maxLength={maxLength}
          autoComplete={fieldKey === "cardNumber" ? "cc-number" : fieldKey === "cardHolder" ? "cc-name" : "cc-csc"}
        />
      );
    }
    return <span className={className}>{displayValue}</span>;
  };

  return (
    <div className="payment-card-container">
      <div className={`payment-card ${effectivelyFlipped ? "flipped" : ""}`}>
        {/* FRONT */}
        <div className={`payment-card-face payment-card-front ${brandClass}`}>
          <div className="payment-card-header">
            <div className="payment-card-chip">
              <svg viewBox="0 0 40 30" width="40" height="30" fill="none">
                <rect x="0" y="0" width="40" height="30" rx="4" fill="#d4af37" />
                <path d="M0 10h10M0 20h10M30 10h10M30 20h10M10 0v30M30 0v30" stroke="#b8860b" strokeWidth="1" />
                <rect x="15" y="10" width="10" height="10" rx="2" stroke="#b8860b" strokeWidth="1" />
              </svg>
            </div>
            {brand !== "unknown" && (
              <BrandIcon brand={brand} className="payment-card-logo" />
            )}
          </div>

          {/* AMEX CVV on front */}
          {showCvvOnFront && (
            <div className="payment-card-amex-cvv">
              <span className="amex-cvv-label">CID</span>
              {renderField(cvv, cvv || "####", "####", "cvv", "amex-cvv-value", 4)}
            </div>
          )}

          <div className="payment-card-number-wrapper">
            {renderField(cardNumber, displayPan, "0000 0000 0000 0000", "cardNumber", "payment-card-number", 19)}
          </div>

          <div className="payment-card-footer">
            <div className="payment-card-holder">
              <span className="payment-card-label">Titular</span>
              {renderField(cardHolder, displayName, "NOMBRE DEL TITULAR", "cardHolder", "payment-card-value", 50)}
            </div>
            <div className="payment-card-expiry">
              <span className="payment-card-label">Válida hasta</span>
              {isInteractiveMode && formConfig && onChange ? (
                <div className="payment-card-expiry-inputs">
                  <input
                    id={formConfig.fields.expiryMonth?.id ?? "expiryMonth"}
                    name={formConfig.fields.expiryMonth?.name ?? "expiryMonth"}
                    className="payment-card-input-exp"
                    value={expiryMonth}
                    onChange={onChange.expiryMonth}
                    onFocus={onFocus?.blur}
                    placeholder="MM"
                    maxLength={2}
                  />
                  <span>/</span>
                  <input
                    id={formConfig.fields.expiryYear?.id ?? "expiryYear"}
                    name={formConfig.fields.expiryYear?.name ?? "expiryYear"}
                    className="payment-card-input-exp"
                    value={expiryYear}
                    onChange={onChange.expiryYear}
                    onFocus={onFocus?.blur}
                    placeholder="AA"
                    maxLength={2}
                  />
                  <span className="payment-card-exp-slash cvv-label">&nbsp;CVV</span>
                  <input
                    id={formConfig.fields.cvv?.id ?? "cvv"}
                    name={formConfig.fields.cvv?.name ?? "cvv"}
                    className="payment-card-input-cvv"
                    value={cvv}
                    onChange={onChange.cvv}
                    onFocus={onFocus?.blur}
                    placeholder="***"
                    maxLength={4}
                    type="password"
                  />
                </div>
              ) : (
                <span className="payment-card-value">{displayExp}</span>
              )}
            </div>
          </div>
        </div>

        {/* BACK */}
        <div className={`payment-card-face payment-card-back ${brandClass}`}>
          <div className="payment-card-magstripe"></div>
          <div className="payment-card-cvv-container">
            <span className="payment-card-label">CVV</span>
            <div className="payment-card-cvv-strip">
              <div className="payment-card-cvv-pattern"></div>
              {!isInteractiveMode && renderField(cvv, cvv || "***", "***", "cvv", "payment-card-cvv-value", 4)}
            </div>
          </div>
          <div className="payment-card-disclaimer">
            Esta tarjeta es intransferible y debe ser firmada por el titular.
          </div>
        </div>
      </div>
    </div>
  );
}
